import React, {
	useCallback,
	useEffect,
	useMemo,
	useState
} from 'react';
import { useHistory } from 'react-router-dom';
import { Form } from 'semantic-ui-react';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import listPlugin from '@fullcalendar/list';

import { handleError } from '../../../../services/utils';

import { getCalendarEvents } from '../../../../api/nora/calendar';

import { useAuthState } from '../../../../contexts/AuthContext';

import {
	useCalendarState,
	useCalendarAction
} from '../../../../contexts/CalendarContext';
import { useUIAction } from '../../../../contexts/UIContext';

import HelpModal from '../../../Shared/HelpModal';
import PageHeader from '../../../Shared/PageHeader';

import styles from './calendar.module.css';

const Calendar = () => {
	const history = useHistory();

	const { user: { permissions } } = useAuthState();
	const {
		calendarEvents,
		eventTags,
		initialized
	} = useCalendarState();
	const { setCalendarEvents } = useCalendarAction();
	const {
		setApiErrorAlert,
		setInfobar,
		setInfobarLoader
	} = useUIAction();

	const [ isLoading, setIsLoading ] = useState( true );
	const [ selectedTags, setSelectedTags ] = useState( [] );
	const [ filteredCalendarEvents, setFilteredCalendarEvents ] = useState( [] );

	useEffect( () => {
		setInfobar( <span>Operations &nbsp;&#9656;&nbsp; Calendar</span> );
	}, [ setInfobar ] );

	const handleTagFilterChange = useCallback( ( e, { value } ) => {
		setSelectedTags( value );
	}, [] );

	useEffect( () => {
		if ( selectedTags.length ) {
			let filteredEvents = calendarEvents.filter( ( { labels } ) => labels.some( ( { id } ) => selectedTags.includes( id ) ) ); // labels on backend

			setFilteredCalendarEvents( filteredEvents );
		} else {
			setFilteredCalendarEvents( calendarEvents );
		}
	}, [ calendarEvents, selectedTags, setFilteredCalendarEvents ] );

	const retrieveCalendarEvents = useCallback( async () => {
		setInfobarLoader( 'Retrieving Calendar Events...' );

		try {
			const data = await getCalendarEvents();

			setCalendarEvents( data[ 0 ].events );
		} catch ( error ) {
			setApiErrorAlert( error );
			throw error;
		}
	}, [
		setInfobarLoader,
		setCalendarEvents,
		setApiErrorAlert
	] );

	useEffect( () => {
		( async () => {
			if ( !initialized ) {
				setInfobarLoader( 'Please wait...' );
			} else {
				if ( permissions.listCalendarEvents.canExecute && permissions.listCalendarEvents.isAvailable ) {
					try {
						await retrieveCalendarEvents();

						setInfobarLoader( '' );
					} catch ( error ) {
						if ( error.message !== 'canceled' ) {
							handleError( error );
							setInfobarLoader( '' );
						}
					}
				} else {
					setInfobarLoader( '' );
				}

				setIsLoading( false );
			}
		} )();
	}, [
		initialized,
		permissions,
		retrieveCalendarEvents,
		setInfobarLoader
	] );

	const events = useMemo( () => filteredCalendarEvents.map( ( { // labels on backend
		id,
		labels,
		end,
		start,
		subject
	} ) => ( {
		...labels.length && {
			backgroundColor: labels[ 0 ].color,
			borderColor: labels[ 0 ].color
		},
		end: new Date( end ),
		id,
		start: new Date( start ),
		title: subject
	} ) ), [ filteredCalendarEvents ] );

	const calendarPugins = useMemo( () => [
		dayGridPlugin,
		listPlugin,
		timeGridPlugin
	], [] );

	const eventClick = useCallback( info => {
		history.push( `/operations/calendar/${ info.event.id }` );
	}, [ history ] );

	const headerToolbar = useMemo( () => ( {
		left: 'prev,next today',
		center: 'title',
		right: 'dayGridMonth,timeGridWeek,timeGridDay,listWeek'
	} ), [] );

	if ( isLoading ) {
		return null;
	}

	return (
		<div className={ styles.calendar }>
			<PageHeader
				actions={ [] }
				title={
					<>
						<span>Calendar</span>
						<HelpModal
							id={ 24 }
							triggerStyles={ { verticalAlign: 'top' } }
						/>
					</>
				}
			/>
			<Form style={{ width: '200px', marginBottom: '1rem' }}>
				<Form.Select
					placeholder="Filter by Event Tags"
					multiple
					onChange={ handleTagFilterChange }
					options={ eventTags }
					value={ selectedTags }
				/>
			</Form>
			<FullCalendar
				eventClick={ eventClick }
				events={ events }
				eventDisplay="block"
				fixedWeekCount={ false }
				headerToolbar={ headerToolbar }
				initialView="dayGridMonth"
				plugins={ calendarPugins }
				stickyHeaderDates
			/>
		</div>
	);
};

export default Calendar;
